// Receptionist · Call Logs — types
// Presentation/state-only data module (mock data + local state, no API or persistence).

/** Which tab of the call-logs archive is active. */
export type CallLogsTabId = 'inbound' | 'outboundPatient';

/** Visual tone used by the pill/config maps. Maps onto the design-system palette. */
export type PillTone = 'positive' | 'neutral' | 'warning' | 'negative' | 'brass';

/** Time-range selector states in the filter bar. */
export type CallLogsTimeRange = 'today' | '7days' | '30days' | 'custom';

/* ---- Inbound enums ------------------------------------------------------ */

export type InboundIntent =
  | 'new_patient_exam'
  | 'scheduling'
  | 'reschedule'
  | 'cancellation'
  | 'emergency_pain'
  | 'post_op_concern'
  | 'billing'
  | 'insurance'
  | 'pricing_inquiry'
  | 'cosmetic_consult'
  | 'hours_location'
  | 'other';

export type InboundOutcome =
  | 'scheduled'
  | 'rescheduled'
  | 'registered'
  | 'answered'
  | 'needs_callback'
  | 'transferred'
  | 'cancelled'
  | 'voicemail'
  | 'dropped';

export type CallSentiment = 'positive' | 'neutral' | 'anxious' | 'frustrated' | 'negative';

/* ---- Outbound (patient / recall) enums ---------------------------------- */

export type RecallTypeSlug =
  | 'appointment_confirmation'
  | 'appointment_reminder'
  | 'post_op_followup'
  | 'hygiene_recall'
  | 'treatment_plan_followup'
  | 'balance_followup'
  | 'reactivation';

export type OutboundOutcome =
  | 'post_op_check_completed'
  | 'post_op_concern_escalated'
  | 'appointment_confirmed'
  | 'appointment_booked'
  | 'appointment_rescheduled'
  | 'appointment_cancelled_reschedule_pending'
  | 'balance_payment_commitment'
  | 'balance_payment_completed'
  | 'balance_discussion_deferred'
  | 'treatment_plan_accepted'
  | 'treatment_plan_declined'
  | 'patient_reactivated_appointment_booked'
  | 'patient_not_interested'
  | 'voicemail_left'
  | 'no_contact';

export type OutboundStatus = 'completed' | 'voicemail' | 'no_answer' | 'failed' | 'cancelled';

/* ---- Shared transcript shape -------------------------------------------- */

export interface TranscriptSpeakerTurn {
  speaker: 'elva' | 'caller';
  text: string;
  time: string;
}

export interface TranscriptReasoningTurn {
  reasoning: string;
}

/** A turn is either a spoken line or an AI-reasoning block. */
export type TranscriptTurn = TranscriptSpeakerTurn | TranscriptReasoningTurn;

export function isReasoningTurn(turn: TranscriptTurn): turn is TranscriptReasoningTurn {
  return (turn as TranscriptReasoningTurn).reasoning !== undefined;
}

/** A label/value fact Elva extracted from the call. */
export interface ExtractedFact {
  label: string;
  value: string;
}

/* ---- Inbound call ------------------------------------------------------- */

export interface InboundCall {
  id: string;
  name: string;
  phone: string;
  isReturning: boolean;
  location: string;
  timeLabel: string;
  duration: string;
  intent: InboundIntent;
  outcome: InboundOutcome;
  sentiment: CallSentiment;
  summary: string[];
  extracted: ExtractedFact[];
  transcript: TranscriptTurn[];
}

/* ---- Outbound (patient / recall) call ----------------------------------- */

export interface PriorStep {
  type: 'email' | 'sms' | 'call';
  result: string;
  sentAt: string;
}

export interface RecallContext {
  sequenceInstanceId: number;
  stepInstanceId: number;
  recallTypeSlug: RecallTypeSlug;
  recallName: string;
  segmentName: string;
  goalType: string;
  priorStepSummary: PriorStep[];
}

export interface AdditionalAction {
  type: string;
  details: string;
  appointmentId?: number;
}

export interface AttemptLogEntry {
  attempt: number;
  time: string;
  result: string;
  outcome: OutboundStatus;
}

export interface OutboundPatientCall {
  id: string;
  name: string;
  phone: string;
  timeLabel: string;
  duration: string;
  recallContext: RecallContext;
  attemptNumber: number;
  maxAttempts: number;
  status: OutboundStatus;
  primaryOutcome: OutboundOutcome;
  additionalActions: AdditionalAction[];
  summary: string[];
  attemptLog: AttemptLogEntry[];
  transcript: TranscriptTurn[] | null;
  extracted?: ExtractedFact[];
  voicemailMessage?: string;
}

/** Union of both row shapes. */
export type CallLogRecord = InboundCall | OutboundPatientCall;

/* ---- KPI strip ---------------------------------------------------------- */

export type KpiTone = 'positive' | 'neutral' | 'warning' | 'negative';

export type KpiIconKey =
  | 'phone-in'
  | 'phone-out'
  | 'check-circle'
  | 'alert'
  | 'clock'
  | 'voicemail'
  | 'shield'
  | 'shield-check';

export interface CallLogsKpi {
  label: string;
  value: string | number;
  sub: string;
  icon: KpiIconKey;
  sentiment: KpiTone;
  delta: number | null;
}

/* ---- View props --------------------------------------------------------- */

export interface CallLogsViewProps {
  /** Tab to open on first render. Defaults to 'inbound'. */
  initialTab?: CallLogsTabId | undefined;
}
